import { z } from 'zod';
import { Id, IsoDateTime, TenantId } from './common.ts';
import { Deadline } from './deadline.ts';

/**
 * Added by 21 step 0 (brief 21 §1, §1b.1, §1b.9; ADR-021): the document register. A `Document` is the register row
 * gates, evidence and receipts cite; its bytes are an Artifact (`artifactId`) or, with a later DMS adapter, an
 * external object (`externalRef`). Falak always keeps the register row. Served by the `DocumentStore` port
 * (`documents-embedded` default: `core_documents` over the Artifact store).
 *
 * Signature proof is only a `Document` with `verification.method: 'e-signature' | 'wet-ink'` and outcome
 * `verified`; an approval decision is never proof that a document was signed (§1b.1). `e-signature` is accepted
 * from a verified upload only (no e-signature adapter in 21).
 *
 * Access (§1b.9): a viewer reads a document when its `aclSubjects` name one of the viewer's subjects (or the viewer
 * uploaded it) on top of `view` on the case; an external principal reads or uploads only documents whose ACL names
 * them or that they uploaded. List routes filter server-side.
 */

/** Added by 21c: document type of a task attachment (`POST /tasks/{taskId}/attachments`). */
export const TASK_ATTACHMENT_DOCUMENT_TYPE = 'task.attachment';

/**
 * Added by 21c: the ACL subject meaning "every principal who may view the case" (core-documents gives it to case
 * viewers; the default ACL of an internal upload is the uploader plus this subject).
 */
export function documentCaseSubject(caseId: string): string {
  return `case:${caseId}`;
}

/** Document classification: the artifact classes plus `pii` (masked for viewers without `act`, 21c item 6). */
export const DocumentClassification = z
  .enum(['public', 'internal', 'confidential', 'restricted', 'pii'])
  .meta({ id: 'DocumentClassification' });
export type DocumentClassification = z.infer<typeof DocumentClassification>;

export const DocumentStatus = z
  .enum(['active', 'superseded', 'retired', 'rejected'])
  .meta({ id: 'DocumentStatus' });
export type DocumentStatus = z.infer<typeof DocumentStatus>;

/** Added by 21c: how a document's bytes came to the register (`e-signature` is accepted on `upload` only). */
export const DocumentOrigin = z.enum(['upload', 'artifact', 'dms']).meta({ id: 'DocumentOrigin' });
export type DocumentOrigin = z.infer<typeof DocumentOrigin>;

export const DocumentVerificationMethod = z
  .enum(['e-signature', 'wet-ink', 'primary-source', 'visual'])
  .meta({ id: 'DocumentVerificationMethod' });
export type DocumentVerificationMethod = z.infer<typeof DocumentVerificationMethod>;

export const DocumentVerification = z
  .object({
    /** Principal id of the verifier. */
    by: z.string().min(1),
    method: DocumentVerificationMethod,
    at: IsoDateTime,
    outcome: z.enum(['verified', 'rejected', 'inconclusive']),
    note: z.string().max(2000).optional(),
    /** Reference to the primary source checked (registry lookup id, URL, call log). */
    evidenceRef: z.string().min(1).max(500).optional(),
  })
  .meta({ id: 'DocumentVerification' });
export type DocumentVerification = z.infer<typeof DocumentVerification>;

/** Methods whose verified outcome is signature proof (§1b.1). */
export const SIGNATURE_VERIFICATION_METHODS: readonly DocumentVerificationMethod[] = [
  'e-signature',
  'wet-ink',
];

/** True when the document is verified signature proof (`e-signature` or `wet-ink`, outcome `verified`). */
export function isSignatureProof(document: Pick<Document, 'verification' | 'status'>): boolean {
  const v = document.verification;
  return (
    document.status === 'active' &&
    v !== undefined &&
    v.outcome === 'verified' &&
    SIGNATURE_VERIFICATION_METHODS.includes(v.method)
  );
}

export const Document = z
  .object({
    id: Id,
    tenantId: TenantId,
    caseId: Id.optional(),
    taskId: Id.optional(),
    /** Pack-declared document type, e.g. `procurement.insurance-certificate`. */
    type: z.string().min(1).max(200),
    title: z.string().min(1).max(300).optional(),
    issuer: z.string().min(1).max(300).optional(),
    /** The issuer's reference number. */
    reference: z.string().min(1).max(300).optional(),
    issuedAt: IsoDateTime.optional(),
    expiresAt: IsoDateTime.optional(),
    classification: DocumentClassification,
    aclSubjects: z.array(z.string().min(1)).default([]),
    /** Principal id (internal or external) of the uploader. */
    uploadedBy: z.string().min(1),
    verification: DocumentVerification.optional(),
    /** The bytes (Artifact store); always set by `documents-embedded`. */
    artifactId: Id,
    status: DocumentStatus,
    /** A DMS object id when a DMS adapter holds the bytes (later adapter). */
    externalRef: z.string().min(1).max(500).optional(),
    /** Document this one replaced (`supersede`). */
    supersedes: Id.optional(),
    supersededBy: Id.optional(),
    mediaType: z.string().min(1).optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
    /** Added by 21c: retention of the bytes (the adapter retires the document once `deleteAfter` passed). */
    retention: z.object({ policy: z.string().min(1), deleteAfter: IsoDateTime.optional() }).optional(),
    /** Added by 21c: `upload` (bytes uploaded with the row), `artifact` (an existing Artifact), `dms` (externalRef). */
    origin: DocumentOrigin.optional(),
    /** Added by 21c (§5 item 6): a `pii` document masked for this viewer (title, issuer, reference removed). */
    redacted: z.boolean().optional(),
    /** Added by 21c: why the document was retired or rejected (`retention`, `scanner: …`, an operator reason). */
    statusReason: z.string().min(1).max(2000).optional(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Document' });
export type Document = z.infer<typeof Document>;

/** Query of `POST /cases/{caseId}/documents` (raw body; `Content-Type` is the file type). */
export const UploadDocumentQuery = z
  .object({
    type: z.string().min(1).max(200),
    filename: z.string().min(1).max(200).default('document'),
    title: z.string().min(1).max(300).optional(),
    issuer: z.string().min(1).max(300).optional(),
    reference: z.string().min(1).max(300).optional(),
    issuedAt: IsoDateTime.optional(),
    expiresAt: IsoDateTime.optional(),
    classification: DocumentClassification.optional(),
    /** Comma-separated ACL subjects (default: the uploader and the case viewers). */
    acl: z.string().min(1).max(2000).optional(),
    /** Id of the document this upload supersedes. */
    supersedes: Id.optional(),
  })
  .meta({ id: 'UploadDocumentQuery' });
export type UploadDocumentQuery = z.infer<typeof UploadDocumentQuery>;

/** Query of `GET /cases/{caseId}/documents`. */
export const DocumentListQuery = z
  .object({ type: z.string().min(1).max(200).optional(), status: DocumentStatus.optional() })
  .meta({ id: 'DocumentListQuery' });
export type DocumentListQuery = z.infer<typeof DocumentListQuery>;

/** Body of `POST /documents/{documentId}/verify` (`by` and `at` come from the session). */
export const VerifyDocumentBody = z
  .object({
    method: DocumentVerificationMethod,
    outcome: z.enum(['verified', 'rejected', 'inconclusive']),
    note: z.string().max(2000).optional(),
    evidenceRef: z.string().min(1).max(500).optional(),
  })
  .meta({ id: 'VerifyDocumentBody' });
export type VerifyDocumentBody = z.infer<typeof VerifyDocumentBody>;

/**
 * Added by document-delete: body of `DELETE /documents/{documentId}`. Delete retires the row and removes the bytes
 * (never a hard delete); `by` and `at` come from the session.
 */
export const DeleteDocumentBody = z
  .object({ reason: z.string().min(1).max(500) })
  .meta({ id: 'DeleteDocumentBody' });
export type DeleteDocumentBody = z.infer<typeof DeleteDocumentBody>;

/** `modules.config.documents` (core-documents; the adapter is `adapters.DocumentStore`, its scanner policy in that config). */
export const DocumentsConfig = z
  .object({
    /** Upload scanner: `reject` (default in `test`), `pass` (pass-through, `dev`). */
    scanner: z.enum(['reject', 'pass']).optional(),
    /** Days before `expiresAt` that `platform.document-expiry` emits `document.expiring`. */
    expiryNoticeDays: z.number().int().positive().max(366).default(30),
  })
  .meta({ id: 'DocumentsConfig' });
export type DocumentsConfig = z.infer<typeof DocumentsConfig>;

export const DOCUMENT_EVENT_TYPES = {
  uploaded: 'document.uploaded',
  verified: 'document.verified',
  expiring: 'document.expiring',
  /** Added by document-delete: the document was retired and its bytes removed (`DELETE /documents/{documentId}`). */
  deleted: 'document.deleted',
} as const;

/** `document.uploaded` / `document.verified` / `document.deleted`. */
export const DocumentEventData = z
  .object({ tenantId: TenantId, caseId: Id.optional(), document: Document, commandId: z.string().min(1) })
  .meta({ id: 'DocumentEventData' });
export type DocumentEventData = z.infer<typeof DocumentEventData>;

/** `document.expiring`: emitted by `platform.document-expiry`; the expiry is a `Deadline`. */
export const DocumentExpiringData = z
  .object({
    tenantId: TenantId,
    caseId: Id.optional(),
    documentId: Id,
    type: z.string().min(1),
    deadline: Deadline,
    commandId: z.string().min(1),
  })
  .meta({ id: 'DocumentExpiringData' });
export type DocumentExpiringData = z.infer<typeof DocumentExpiringData>;
